import { TDay } from 'ui/components/Admin/Shifts/ShiftEditDuplicate/utils/types'
import { WEEKDAY_MAP } from '@src/features/shift-post-v2/components/recurring-day-picker'
import { INewEditModalFormValues } from './new-edit-modal.form'

/**
 * Converts day_start and day_end to ISO strings.
 * This function is necessary because moment objects cannot be serialized.
 *
 * @param values Formik values
 * @returns Formik values with day_start and day_end converted to ISO strings
 */
export const momentToISOString = (values: INewEditModalFormValues) => {
  return {
    ...values,
    days: values.days.map(({ day_start, day_end, ...rest }) => ({
      ...rest,
      day_start: day_start.toISOString(),
      day_end: day_end.toISOString(),
    })),
  }
}

export const getDaysComparedToSelectedWeekdays = (days: TDay[], selectedWeekdays: string[]) => {
  // If selectedWeekdays is empty, return days as they are
  if (selectedWeekdays.length === 0) {
    return days
  }

  // Convert selectedWeekdays to numbers for comparison
  const selectedWeekdayNumbers = selectedWeekdays
    .map((day) => WEEKDAY_MAP[day])
    .filter((num) => num !== undefined)

  // Check if days follow the pattern of selectedWeekdays
  let followsPattern = true

  for (const day of days) {
    const dayOfWeek = day.day_start.day() // 0-6, where 0 is Sunday
    const isSelectedWeekday = selectedWeekdayNumbers.includes(dayOfWeek)

    // If this day should be working according to selectedWeekdays but isn't,
    // or if this day shouldn't be working according to selectedWeekdays but is,
    // then the pattern doesn't match
    if (isSelectedWeekday !== day.working) {
      followsPattern = false
      break
    }
  }

  // If the pattern matches, return only the working days (omit non-working days)
  if (followsPattern) {
    return days.filter((day) => day.working)
  }

  // If the pattern doesn't match, return days as they are
  return days
}

export const getWeeklyPattern = (days: TDay[]) => {
  // If no days, return empty array
  if (days.length === 0) {
    return []
  }

  // Group days by day of week and check if any are working
  const weeklyPattern: { [key: number]: boolean } = {}

  for (const day of days) {
    const dayOfWeek = day.day_start.day() // 0-6, where 0 is Sunday

    // If we haven't seen this day of week yet, record it
    if (weeklyPattern[dayOfWeek] === undefined) {
      weeklyPattern[dayOfWeek] = day.working
    } else {
      // If we've seen this day before, mark as working if any instance is working
      if (day.working) {
        weeklyPattern[dayOfWeek] = true
      }
    }
  }

  // Convert the pattern to an array of weekday strings
  const patternWeekdays: string[] = []

  // Create reverse mapping from numbers to weekday strings
  const reverseWeekdayMap = Object.entries(WEEKDAY_MAP).reduce((acc, [day, num]) => {
    acc[num] = day
    return acc
  }, {} as { [key: number]: string })

  // Add weekdays that have at least one working day
  for (const [dayNum, hasWorkingDay] of Object.entries(weeklyPattern)) {
    if (hasWorkingDay) {
      const weekdayString = reverseWeekdayMap[Number(dayNum)]
      if (weekdayString) {
        patternWeekdays.push(weekdayString)
      }
    }
  }

  return patternWeekdays
}
